/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Pagination,
  Table,
  Typography,
  useTheme,
  Icon,
} from '@quickbit/qb-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { QBLoader, TruncatedString } from 'components';
import { useOrganisation } from 'contexts';
import { getSettlementReports } from 'tabs/AccountingSettlement/api';
const PAGE_SIZE = 16;

const HEADERS = [
  { label: 'Timespan', cellKey: 'timespan', sort: false },
  { label: 'Id', cellKey: 'uuid', sort: false },
  { label: 'Processing Fee', cellKey: 'processingFee', sort: false },
  { label: 'Total sales volume', cellKey: 'totalSalesVolume', sort: false },
  { label: 'Total', cellKey: 'total', sort: false },
  { label: 'Balance', cellKey: 'balance', sort: false },
  { label: 'Settled', cellKey: 'processed', sort: false },
];

export const History = () => {
  const {
    selectedMerchant: { merchantId },
  } = useOrganisation();
  const navigate = useNavigate();
  const { spacing, palette } = useTheme();
  const [page, setPage] = useState(1);

  const [{ value, loading }, refresh] = useAsyncFn(
    () =>
      getSettlementReports({
        pagination: { size: PAGE_SIZE, page },
        filters: {
          merchantId: merchantId,
        },
      }),
    [page, merchantId]
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const rows = useMemo(
    () =>
      value?.reports.map((report) => {
        const totalSettlementAmount = (
          report.adjustments.reduce(
            (acc, curr) => acc + curr.amount,
            report.settlementAmount
          ) + (report.refundDebt ? report.refundDebt : 0)
        ).toFixed(2);
        return {
          value: {
            timespan: `${report.startTime.toLocaleDateString()} - ${report.endTime.toLocaleDateString()}`,
            uuid: <TruncatedString text={report.uuid} />,
            processingFee: report.processingFee,
            totalSalesVolume: report.totalAmount,
            total: totalSettlementAmount,
            balance: report.amountRemainingToBePaid.toFixed(2),
            processed: report.processed ? 'Yes' : 'No',
            id: report.uuid,
          },
        };
      }),
    [value]
  );

  if (!value) return <QBLoader />;
  return (
    <>
      <Header>
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div css={{ display: 'flex', gap: spacing.xxs }}>
            <Typography variant="subtitle">Accounting</Typography>
          </div>
          {loading && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
        </div>
      </Header>
      <>
        <Table
          headers={HEADERS}
          rows={rows ?? []}
          onRowClick={(row) => {
            void navigate(row.id as string);
          }}
        />
        <div css={{ marginTop: spacing.xxs }}>
          <Pagination
            currentPage={page}
            onChange={setPage}
            numberOfElements={value.aggregatedData.count}
            pageSize={PAGE_SIZE}
          />
        </div>
      </>
    </>
  );
};
